// @ts-strict-ignore
import * as React from 'react';
import { RuntimeLoader } from '@rive-app/react-canvas';
import riveWASMResource from '@rive-app/canvas/rive.wasm';
import { sprig } from '@sprig-technologies/sprig-browser';
import { ModalReact } from 'common/components/modal';
import { trackHotjarEvent } from 'common/util/hotjar';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { userCookieUtils } from 'common/report/util/user-identity-from-cookie';
import { isProdEnv, isTestEnv } from 'common/report/util/env';
import { getLocation } from 'common/util/url';
import { getProjectId } from 'common/report/util';
import { useLocalStorageManager } from 'common/contexts/react/storage-manager-context';
import { useImmer, useMountEffect } from '../utils';
import { CompleteStep, SetupGuideContext, SetupGuideType, Step, VisibilityContext } from './types';
import { track, TrackingEvent, TrackingProperty } from './tracking';
import { IMMERSIVE_ONBOARDING_URL_PARAM, SPRIG_QUALIFIER_V2 } from './constants';
import { ImmersiveOnboardingMobile } from './immersive-onboarding-mobile';
import { ApiClient } from './api-client';
import { ModalContentPhase2 } from './modal-content/modal-content-phase2';
const Sprig = sprig.configure({
  environmentId: isProdEnv() ? "9sZai6GO-WSS" : "nwR-Y3CwEnJH"
});
export const ImmersiveOnboarding = () => {
  const currentProject = useCurrentProject();
  const localStorageManager = useLocalStorageManager();
  const [open, setOpen] = React.useState(true);
  const [step, setStep] = React.useState<Step>(null);
  const [setupGuideInfo, setSetupGuideInfo] = React.useState<SetupGuideType>(null);
  const [isIntegrated, setIsIntegrated] = React.useState(false);
  const [visibility, setVisibility] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(true);
  useMountEffect(() => {
    const callback = () => {
      setOpen(true);
    };
    document.addEventListener("onImmersiveOnboardingInit", callback);
    return () => {
      document.removeEventListener("onImmersiveOnboardingInit", callback);
    };
  });
  useMountEffect(() => {
    // Initialize hotjar before loading is done to collect data on loading status
    trackHotjarEvent("Immersive Onboarding");
    // GOOT(Sonya): WASM is retrieved via window.fetch. Ensure the asset url has correct protocol set for window.fetch
    // Without this, the request runs into CORS issue on production
    RuntimeLoader.setWasmUrl(new URL(riveWASMResource, "https://example.com" /** This is only to set "https:" in the url passed to fetch */).toString());
    setIsLoading(true);
    const projectId = parseInt(getProjectId(), 10);
    ApiClient.get(projectId).then(setupGuideInfoResponse => {
      setSetupGuideInfo(setupGuideInfoResponse);
      const searchParams = new URLSearchParams(getLocation().search);

      // Regardless of setup guide status, show Welcome step on their first visit
      // This is working under the assumption that the first load of immersive modal
      // is driven by "immersive-onboarding" query parameter.
      if (searchParams.has(IMMERSIVE_ONBOARDING_URL_PARAM)) {
        var _currentProject$get$v;
        if (searchParams.get(IMMERSIVE_ONBOARDING_URL_PARAM) === Step.Implement) {
          setStep(Step.Implement);
        } else if (currentProject !== null && currentProject !== void 0 && (_currentProject$get$v = currentProject.get().value) !== null && _currentProject$get$v !== void 0 && _currentProject$get$v.hasIntegratedArb) {
          if (!isIntegrated) setIsIntegrated(true);
          setStep(Step.Implement);
        } else {
          setStep(Step.Welcome);
        }
      } else {
        if ((setupGuideInfoResponse === null || setupGuideInfoResponse === void 0 ? void 0 : setupGuideInfoResponse.stepsCompleted.size) > 0) {
          var _setupGuideInfoRespon, _setupGuideInfoRespon2;
          if (isIntegrated) {
            setStep(Step.Implement);
          } else if (setupGuideInfoResponse !== null && setupGuideInfoResponse !== void 0 && (_setupGuideInfoRespon = setupGuideInfoResponse.stepsCompleted) !== null && _setupGuideInfoRespon !== void 0 && _setupGuideInfoRespon.has(Step.Implement)) {
            if (!isIntegrated) setIsIntegrated(true);
            setStep(Step.Implement);
          } else if (setupGuideInfoResponse !== null && setupGuideInfoResponse !== void 0 && (_setupGuideInfoRespon2 = setupGuideInfoResponse.stepsCompleted) !== null && _setupGuideInfoRespon2 !== void 0 && _setupGuideInfoRespon2.has(Step.Events)) {
            setStep(Step.Implement);
          } else {
            setStep(Step.Events);
          }
        } else {
          setStep(Step.Welcome);
        }
      }
      setIsLoading(false);
    });
    track(TrackingEvent.ModalLoaded);
  });
  const [completed, setCompleted] = useImmer<CompleteStep>({
    [Step.Welcome]: false,
    [Step.Events]: false,
    [Step.Implement]: false
  });
  React.useEffect(() => {
    if (setupGuideInfo) {
      setCompleted(draft => {
        var _setupGuideInfo$steps, _setupGuideInfo$steps2, _setupGuideInfo$steps3, _setupGuideInfo$steps4;
        draft.welcome = setupGuideInfo === null || setupGuideInfo === void 0 || (_setupGuideInfo$steps = setupGuideInfo.stepsCompleted) === null || _setupGuideInfo$steps === void 0 ? void 0 : _setupGuideInfo$steps.has(Step.Welcome);
        draft.events = setupGuideInfo === null || setupGuideInfo === void 0 || (_setupGuideInfo$steps2 = setupGuideInfo.stepsCompleted) === null || _setupGuideInfo$steps2 === void 0 ? void 0 : _setupGuideInfo$steps2.has(Step.Events);
        draft.implement = setupGuideInfo === null || setupGuideInfo === void 0 || (_setupGuideInfo$steps3 = setupGuideInfo.stepsCompleted) === null || _setupGuideInfo$steps3 === void 0 ? void 0 : _setupGuideInfo$steps3.has(Step.Implement);
        draft.explore = setupGuideInfo === null || setupGuideInfo === void 0 || (_setupGuideInfo$steps4 = setupGuideInfo.stepsCompleted) === null || _setupGuideInfo$steps4 === void 0 ? void 0 : _setupGuideInfo$steps4.has(Step.Explore);
      });
    }
  }, [setupGuideInfo]);
  const openModal = React.useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = React.useCallback(() => {
    setOpen(false);
    const url = new URL(getLocation().href);
    url.searchParams.delete(IMMERSIVE_ONBOARDING_URL_PARAM); // remove query params to prevent immersive onboarding from showing up on app change
    window.history.replaceState({}, "", "".concat(url.pathname).concat(url.search));
    if (isIntegrated && !isTestEnv()) {
      var _userCookieUtils$getU;
      Sprig("identifyAndTrack", {
        eventName: "Immersive Post",
        userId: (_userCookieUtils$getU = userCookieUtils.getUserIdentityFromCookie()) === null || _userCookieUtils$getU === void 0 ? void 0 : _userCookieUtils$getU.id.toString(),
        showSurveyCallback: () => {
          if (localStorageManager.getItem(SPRIG_QUALIFIER_V2)) {
            return Promise.resolve(false);
          } else {
            localStorageManager.setItem(SPRIG_QUALIFIER_V2, "true");
            return Promise.resolve(true);
          }
        }
      });
    }

    // TODO: once React Router is used everywhere, we'll be able to use that
    track(TrackingEvent.SkipForNow, {
      [TrackingProperty.CurrentStep]: step
    });
  }, [step]);
  return <VisibilityContext.Provider value={{
    visibility,
    setVisibility
  }} data-sentry-element="unknown" data-sentry-component="ImmersiveOnboarding" data-sentry-source-file="immersive-onboarding.tsx">
      <SetupGuideContext.Provider value={{
      modalManager: {
        openModal,
        closeModal
      },
      step,
      setStep,
      setupGuideInfo,
      setSetupGuideInfo,
      isIntegrated,
      setIsIntegrated,
      completed
    }} data-sentry-element="unknown" data-sentry-source-file="immersive-onboarding.tsx">
        <ModalReact open={open} grow-to-content style={{
        display: !visibility && "none"
      }} data-sentry-element="ModalReact" data-sentry-source-file="immersive-onboarding.tsx">
          <ModalContentPhase2 isLoading={isLoading} data-sentry-element="ModalContentPhase2" data-sentry-source-file="immersive-onboarding.tsx" />
        </ModalReact>
        <ImmersiveOnboardingMobile data-sentry-element="ImmersiveOnboardingMobile" data-sentry-source-file="immersive-onboarding.tsx" />
      </SetupGuideContext.Provider>
    </VisibilityContext.Provider>;
};